// src/pages/TransactionsUploadPage.js
import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import {
  uploadTransactions,
  classifyTransactions,
  reconcileTransactions,
  fetchTransactionTimeseries,
  finalizeTransactionsToDashboard,
  // NEW: wipe stale payloads & caches so UI reloads with transactions dataset
  wipeFrontendCaches,
  clearForecastCache,
  clearModelInsightsCache,
  clearStrategyCache,
} from '../utils/api';
import '../styles/Transactions.css';

import '../components/UploadForm.css'; // reuse the same styling as Upload

export default function TransactionsUploadPage() {
  const {
    token, user,
    setCsvData, setColumnTypes, setCompanyName,
    setHasUploaded, setIsDemo, setUploadedFilename,
    // NEW: ensure feature tabs recompute with new dataset
    setForecastData, setStrategyData, setModelInsightsData
  } = useAppContext();

  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState('Choose a CSV to begin');

  const chooseFile = (f) => {
    if (!f) return;
    const okMime = ['text/csv', 'application/vnd.ms-excel', 'application/octet-stream'].includes(f.type);
    const okExt  = /\.csv$/i.test(f.name);
    if (!okMime && !okExt) {
      setError('Please upload a .csv file.');
      setFile(null);
      return;
    }
    setError(null);
    setFile(f);
  };

  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) chooseFile(e.dataTransfer.files[0]);
  };

  const runPipeline = async () => {
    if (!file || busy) return;
    setBusy(true); setError(null);

    // Pre-check server limits (same as normal UploadForm)
    try {
      const res = await fetch('/api/account/limits', {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const limits = await res.json();
      if (limits.uploads_remaining === 0) {
        setError(limits.trial_active
          ? 'Your trial includes 1 upload. Upgrade to Clairifi Core to continue.'
          : 'Monthly upload limit reached. Upgrade or wait until next cycle.');
        setBusy(false);
        return;
      }
      if (limits.plan !== 'core' && !limits.trial_active) {
        setError('Your trial has ended. Upgrade to Clairifi Core to continue.');
        setBusy(false);
        return;
      }
    } catch (e) {
      console.warn('[TransactionsUploadPage] limits pre-check failed:', e);
      // Let it continue; backend will still enforce.
    }

      try {
        setProgress('Uploading…');
        const up = await uploadTransactions(file, { token, userId: user?.id });
        if (!up?.batch_id) {
          throw new Error(up?.detail || 'Upload failed: no batch id returned.');
        }
        const batchId = up.batch_id;



      setUploadedFilename(file.name);

      setProgress('Classifying…');
      await classifyTransactions(batchId, { token, use_gpt: true });

      setProgress('Reconciling & aggregating…');
      await reconcileTransactions(batchId, { token });
      await fetchTransactionTimeseries(batchId, { token }); // primes aggregates

      setProgress('Finalizing to Dashboard…');
      const fin = await finalizeTransactionsToDashboard(batchId, { token });

      // Adopt the new, transactions-derived dataset
      if (Array.isArray(fin?.data)) setCsvData(fin.data);
      if (fin?.column_types)       setColumnTypes(fin.column_types);
      if (fin?.company)            setCompanyName(fin.company);

      // ---- IMPORTANT: clear all stale caches bound to previous dataset ----
      try {
        wipeFrontendCaches();
        if (user?.id) {
          clearForecastCache(user.id);
          clearModelInsightsCache(user.id);
          clearStrategyCache(user.id);
        }
      } catch { /* non-fatal */ }

      // Reset feature payloads so pages recompute with the new rows/column types
      try {
        setForecastData?.(null);
        setStrategyData?.(null);
        setModelInsightsData?.(null);
      } catch { /* non-fatal */ }

      // Mark upload and route to dashboard
      try { localStorage.setItem('clairifi:hasUploaded', '1'); } catch {}
      setHasUploaded(true);
      setIsDemo(false);

      navigate('/dashboard');
      window.scrollTo?.(0, 0);

    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  };


  // When a file is chosen, immediately run the pipeline
  const onChoose = (f) => { chooseFile(f); setTimeout(() => runPipeline(), 0); };

  return (
    <div className="upload-form-container">
      <div className="upload-form-card">
        <h1 className="upload-title">Upload Transactions</h1>
        <p className="upload-subtitle">Drop a bank/credit-card CSV. We’ll classify and send it to your Dashboard.</p>

        {error && <div className="upload-error" role="alert">⚠️ {error}</div>}
        <div className={`upload-dropzone ${dragActive ? 'is-dragging' : ''} ${file ? 'has-file' : ''}`}
             onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
             onDragOver={(e)  => { e.preventDefault(); setDragActive(true); }}
             onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
             onDrop={onDrop}
             role="button"
             tabIndex={0}
             onClick={() => inputRef.current?.click()}
             onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click(); }}
             aria-label="Drag and drop CSV here or click to browse">
          <svg className="upload-icon" width="48" height="48" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M12 16V4m0 0l-4 4m4-4l4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M20 16.5a3.5 3.5 0 01-3.5 3.5h-9A3.5 3.5 0 014 16.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          {!file ? (
            <>
              <p className="dz-title">Drag & drop your CSV</p>
              <p className="dz-subtitle">or <span className="dz-browse">browse</span> to choose a file</p>
              <p className="dz-hint">Accepted: .csv</p>
            </>
          ) : (
            <div className="file-chip" title={file.name}>
              <span className="file-name">{file.name}</span>
              <button type="button" className="chip-x" onClick={(e) => { e.stopPropagation(); setFile(null); }}>
                ×
              </button>
            </div>
          )}
        </div>

        <input
          ref={inputRef}
          className="upload-input-hidden"
          type="file"
          accept=".csv,text/csv"
          onChange={(e) => onChoose(e.target.files?.[0] || null)}
          aria-label="Choose CSV file"
        />

        <div style={{ marginTop: 10, color: '#667' }}>
          {busy ? <span>⏳ {progress}</span> : <span>{progress}</span>}
        </div>

        <div className="upload-actions">
          <button className="upload-btn" disabled={busy || !file} onClick={runPipeline}>
            {busy ? 'Working…' : 'Upload & Process'}
          </button>
        </div>
      </div>
    </div>
  );
}
